import { VehiclesListType } from "../vehiclesAPI";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany, selectManager } from "../../homeSlice";
import {
  selectVehicleTypeUpdatedStatus,
  updateVehicleTypeAsync,
  vehiclesSlice,
} from "../vehiclesSlice";
import { selectElectricVehicles } from "../../electricCharging/electricVehiclesSlice";
import {
  SelectDS,
  TextCapitalized,
  Tag,
  SidePanelDS,
  triggerToast,
  Option,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";

export const UpdateTypeSection = (props: { vehicle: VehiclesListType }) => {
  const { t } = useTranslation();
  const manager = useAppSelector(selectManager);
  const dispatch = useAppDispatch();
  const [vehicleType, setVehicleType] = useState<"PERSONAL" | "SHARED" | null>(
    null,
  );
  const { vehicle } = props;
  const company = useAppSelector(selectCompany);
  const vehicleTypeUpdatedStatus = useAppSelector(
    selectVehicleTypeUpdatedStatus,
  );
  const electricVehicles = useAppSelector(selectElectricVehicles);

  const isConnectedToHomeCharging = useMemo(
    () =>
      !!company?.hasHomeCharging &&
      electricVehicles.some(
        (electricVehicle) =>
          electricVehicle.uuid === vehicle.uuid &&
          electricVehicle.status === "CONNECTED",
      ),
    [company?.hasHomeCharging, electricVehicles, vehicle],
  );

  useEffect(() => {
    if (vehicleTypeUpdatedStatus === "success") {
      triggerToast(t("vehicles.updatedType.success") || "", "valid");
      dispatch(vehiclesSlice.actions.resetVehicleUpdatedTypeStatus());
    } else if (vehicleTypeUpdatedStatus === "failed") {
      triggerToast(t("vehicles.updatedType.failure") || "", "error");
      dispatch(vehiclesSlice.actions.resetVehicleUpdatedTypeStatus());
    }
  }, [vehicleTypeUpdatedStatus, t, dispatch]);

  const isVehicleUpdatableWithType: boolean = useMemo(() => {
    if (vehicle && vehicle.vehicleType !== "OUT_OF_FLEET") {
      setVehicleType(vehicle.vehicleType);
      return true;
    }
    return false;
  }, [vehicle]);

  const isUserManager = useMemo(
    () => manager && manager.role === "USER",
    [manager],
  );

  const optionsVehicleType = useMemo(() => {
    if (!vehicle || vehicle.vehicleType === "OUT_OF_FLEET") return [];
    return [
      {
        value: "SHARED",
        label: t("vehicles.type.SHARED"),
        disabled: vehicle.vehicleType === "SHARED",
      },
      {
        value: "PERSONAL",
        label: t("vehicles.type.PERSONAL"),
        disabled: vehicle.vehicleType === "PERSONAL",
      },
    ] as Option<"SHARED" | "PERSONAL">[];
  }, [t, vehicle]);

  const valueVehicleType = useMemo(
    () => optionsVehicleType.find((option) => option.value === vehicleType),
    [optionsVehicleType, vehicleType],
  );

  if (!company) {
    return;
  }

  return (
    <SidePanelDS.SectionRow
      label={
        <SectionRowLabel>
          <TextCapitalized>{t("vehicles.sidepanel.type")}</TextCapitalized>
        </SectionRowLabel>
      }
      value={
        <>
          {isVehicleUpdatableWithType && !isUserManager ? (
            <SelectDS
              label={""}
              options={optionsVehicleType}
              placeholder={t(`vehicles.type.${vehicle.vehicleType}`) || ""}
              onChange={(selectedOption) => {
                selectedOption &&
                  void dispatch(
                    updateVehicleTypeAsync({
                      vehicleUuid: vehicle.uuid,
                      vehicleType: selectedOption.value,
                      companyName: company.name,
                    }),
                  );
                selectedOption && setVehicleType(selectedOption.value);
              }}
              value={valueVehicleType}
              allWidth
              withoutSpacerBottom
              disabled={isConnectedToHomeCharging}
              error={
                isConnectedToHomeCharging
                  ? t("vehicles.updatedType.connectedToHomeCharging")
                  : undefined
              }
            />
          ) : (
            <Tag
              text={t(`vehicles.type.${vehicle.vehicleType}`)}
              borderColor={colors["colors/borders/cells/cells"]}
              backgroundColor={
                colors["colors/surfaces/background/background_level0"]
              }
              textColor={colors["colors/text/darkGrey"]}
            />
          )}
        </>
      }
    />
  );
};

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;
